import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";
import { FIRST_COMMENT_MAX } from "./social";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** A link typed for a post: only https. Rows already stored as http keep working, because nothing re-parses them against this. */
const PostLinkUrl = z.url({ protocol: /^https$/ }).max(2_000);

export const CREATIVE_STATUSES = ["draft", "in_review", "approved", "rejected", "scheduled", "publishing", "published", "failed", "needs_review", "expired", "withdrawn"] as const;
export const CreativeStatus = z.enum(CREATIVE_STATUSES);
export type CreativeStatus = z.infer<typeof CreativeStatus>;

/** What a variant may do differently from its siblings. */
export const VARIANT_DIMENSIONS = ["hook", "angle", "length", "cta"] as const;
export const VariantDimension = z.enum(VARIANT_DIMENSIONS);
export type VariantDimension = z.infer<typeof VariantDimension>;

/**
 * Where on the channel a creative appears. Everything made before this column exists stays `feed`.
 * Only Instagram and Facebook have the short formats a person can ask for here; TikTok and Shorts are always vertical
 * and are a placement of the card, not a choice in the brief.
 */
export const CREATIVE_SURFACES = ["feed", "story", "reel"] as const;
export const CreativeSurface = z.enum(CREATIVE_SURFACES);
export type CreativeSurface = z.infer<typeof CreativeSurface>;

const SURFACES_BY_PLATFORM: Partial<Record<string, readonly CreativeSurface[]>> = {
  instagram: ["feed", "story", "reel"],
  facebook: ["feed", "story"],
};

export const surfacesOfPlatform = (platform: string): readonly CreativeSurface[] => SURFACES_BY_PLATFORM[platform] ?? ["feed"];

/** The surfaces every one of these platforms can carry: what a brief aimed at several channels may ask for. */
export const surfacesOfPlatforms = (platforms: readonly string[]): readonly CreativeSurface[] =>
  CREATIVE_SURFACES.filter((surface) => platforms.every((platform) => surfacesOfPlatform(platform).includes(surface)));

/** Which picture a creative carries, the choice made by the automatic pipeline. */
export const VISUAL_CHOICES = ["own_photo", "own_photo_text", "tagged_photo", "tagged_photo_text", "card", "template", "none"] as const;
export const VisualChoice = z.enum(VISUAL_CHOICES);
export type VisualChoice = z.infer<typeof VisualChoice>;

export const CREATIVE_RUN_SOURCES = ["brief", "plan_item", "schedule"] as const;
export const GENERATION_STATUSES = ["queued", "running", "done", "failed"] as const;
export const APPROVAL_VIAS = ["user", "link", "api_key", "agent"] as const;
export const ApprovalVia = z.enum(APPROVAL_VIAS);
export type ApprovalVia = z.infer<typeof ApprovalVia>;

export const TEMPLATE_KINDS = ["hook", "button", "signature", "hashtag_set", "visual"] as const;
export const TemplateKind = z.enum(TEMPLATE_KINDS);
export type TemplateKind = z.infer<typeof TemplateKind>;

export const NOTIFY_CHANNELS = ["email", "telegram", "whatsapp", "sms", "push"] as const;
export const NotifyChannelName = z.enum(NOTIFY_CHANNELS);
export type NotifyChannelName = z.infer<typeof NotifyChannelName>;

export const AUTONOMY_LEVELS = ["human_only", "agent_prescreen", "agent_approve"] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];

/** How many clean human approvals an archetype needs before "agent approves" can be turned on. */
export const CLEAN_APPROVALS_FOR_AGENT = 20;
/** How long a phone link stays usable. */
export const APPROVAL_LINK_DAYS = 7;
/** What a yes is worth if the post has not gone out by then: a text approved two months ago no longer speaks for anyone. */
export const APPROVAL_VALID_DAYS = 30;

const Language = z.string().trim().min(2).max(10);
const Reason = z.string().trim().min(3).max(1_000);

// ---------------------------------------------------------------------------
// Generate
// ---------------------------------------------------------------------------

export const GenerateRequest = z
  .object({
    /** What the post should say. Either this or a topic. */
    brief: z.string().trim().min(3).max(4_000).nullable().optional(),
    topicId: Uuid.nullable().optional(),
    archetype: z.string().trim().min(1).max(64),
    channelIds: z.array(Uuid).min(1).max(12),
    /** Feed, Story or Reel. A channel that has no such place is refused when the run is asked for. */
    surface: CreativeSurface.default("feed"),
    languages: z.array(Language).min(1).max(6),
    variants: z.number().int().min(1).max(6).default(2),
    varies: z.array(VariantDimension).min(1).max(4).default(["hook", "angle"]),
    factIds: z.array(Uuid).max(200).optional(),
    /** Own templates this generation should use. */
    templateIds: z.array(Uuid).max(40).optional(),
    /** Who this is written for: their pains and objections reach the writer. */
    profileId: Uuid.nullable().optional(),
    /** What it promises: the writer keeps to it, its facts and landing page stand in when the run names none. */
    angleId: Uuid.nullable().optional(),
    scheduledFor: IsoDateTime.nullable().optional(),
    linkUrl: PostLinkUrl.nullable().optional(),
    linkLabel: z.string().trim().max(60).nullable().optional(),
    /** Asks the same thing twice and gets one run. */
    idempotencyKey: z.string().trim().min(8).max(100).optional(),
  })
  .strict()
  .refine((value) => Boolean(value.brief) || Boolean(value.topicId), { message: "brief or topicId is required", path: ["brief"] });
export type GenerateRequest = z.input<typeof GenerateRequest>;

export const GenerationRunDto = z.object({
  id: Uuid,
  source: z.enum(CREATIVE_RUN_SOURCES),
  status: z.enum(GENERATION_STATUSES),
  planId: Uuid.nullable(),
  planItemId: Uuid.nullable(),
  creativeCount: z.number().int(),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
});
export type GenerationRunDto = z.infer<typeof GenerationRunDto>;

// ---------------------------------------------------------------------------
// Creatives and the queue
// ---------------------------------------------------------------------------

export const CreativeMediaDto = z.object({
  assetId: Uuid,
  kind: z.enum(["image", "video", "document"]),
  mimeType: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  altText: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  /** True when an AI drew it: the label travels with the picture. */
  aiGenerated: z.boolean(),
  /** A video's length. */
  durationMs: z.number().int().nullish(),
  /** A document's pages. */
  pages: z.number().int().nullish(),
  /** What a document post shows above its pages. */
  title: z.string().nullish(),
  /** A video's chosen cover. */
  coverAssetId: z.string().nullish(),
});
export type CreativeMediaDto = z.infer<typeof CreativeMediaDto>;

export const CheckFindingDto = z.object({
  code: z.string(),
  severity: z.enum(["red", "yellow"]),
  message: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
  field: z.string().nullable().optional(),
  fix: z.unknown().optional(),
});
export type CheckFindingDto = z.infer<typeof CheckFindingDto>;

export const CreativeStepDto = z.object({
  id: Uuid,
  revision: z.number().int(),
  step: z.string(),
  outcome: z.string(),
  reason: z.string().nullable(),
  detail: z.unknown(),
  actorName: z.string(),
  at: IsoDateTime,
});
export type CreativeStepDto = z.infer<typeof CreativeStepDto>;

/** Says which picture the pipeline chose and why, in stable codes. What it tried and passed over is in the steps. */
export const CreativeVisualChoiceDto = z.object({
  kind: VisualChoice,
  assetId: Uuid.nullable(),
  /** The fact-linked or tag-matched subject, or the reason nothing could be drawn. */
  reason: z.string(),
});
export type CreativeVisualChoiceDto = z.infer<typeof CreativeVisualChoiceDto>;

export const CreativeDto = z.object({
  id: Uuid,
  runId: Uuid.nullable(),
  topicId: Uuid.nullable(),
  channelId: Uuid.nullable(),
  platform: z.string(),
  surface: CreativeSurface,
  visualChoice: CreativeVisualChoiceDto.nullable(),
  language: z.string(),
  archetype: z.string(),
  variantIndex: z.number().int(),
  varied: z.record(z.string(), z.unknown()),
  status: CreativeStatus,
  revision: z.number().int(),
  body: z.string(),
  hashtags: z.array(z.string()),
  linkUrl: z.string().nullable(),
  linkLabel: z.string().nullable(),
  /** LinkedIn: what goes in the first comment. */
  firstComment: z.string().nullable(),
  bodyRendered: z.string(),
  charCount: z.number().int(),
  truncated: z.boolean(),
  imageText: z.array(z.string()),
  media: z.array(CreativeMediaDto),
  aiGenerated: z.boolean(),
  humanEdited: z.boolean(),
  regulated: z.boolean(),
  findings: z.array(CheckFindingDto),
  compliance: z.unknown(),
  verdict: z.unknown(),
  decision: z.unknown(),
  blocksApproval: z.boolean(),
  regenerationAttempts: z.number().int(),
  spotCheck: z.boolean(),
  scheduledFor: IsoDateTime.nullable(),
  approvedAt: IsoDateTime.nullable(),
  approvedByName: z.string().nullable(),
  rejectReason: z.string().nullable(),
  rejectedAt: IsoDateTime.nullable(),
  rejectedByName: z.string().nullable(),
  socialPostId: Uuid.nullable(),
  publishedAt: IsoDateTime.nullable(),
  permalink: z.string().nullable(),
  dryRun: z.boolean(),
  lastErrorCode: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type CreativeDto = z.infer<typeof CreativeDto>;

/** A queue card: the creative, the exact preview the channel would show and how it got here. */
/**
 * A post taken back. `manualRequired`: the platform did not take it down, so it is still public and nothing here changed. `manualReason` says why,
 * `permalink` where to go, and the same call with `confirmedGone` closes it once a person has removed it.
 */
export const WithdrawCreativeResponse = CreativeDto.extend({
  manualRequired: z.boolean(),
  manualReason: z.string().nullable(),
  permalink: z.string().nullable(),
});
export type WithdrawCreativeResponse = z.infer<typeof WithdrawCreativeResponse>;

export const QueueCardDto = z.object({
  creative: CreativeDto,
  /** The preview layout from `packages/previews`; null on channels with no preview model. */
  preview: z.unknown(),
  steps: z.array(CreativeStepDto),
  /** No picture yet only because the run is still making one: nothing to fix, and approval waits. */
  visualPending: z.boolean(),
  /** The post is out and its first comment is not: a person can post it by hand. */
  firstCommentMissed: z.object({ text: z.string(), permalink: z.string().nullable(), code: z.string().nullable() }).nullable(),
});
export type QueueCardDto = z.infer<typeof QueueCardDto>;

export const CreativePage = pageOf(CreativeDto);
export const QueueListQuery = IdPageQuery.extend({
  /** One, several, or several separated by commas: a query string carries a single value. */
  status: z.preprocess((value) => (typeof value === "string" && value.includes(",") ? value.split(",") : value), z.union([CreativeStatus, z.array(CreativeStatus)]).optional()),
  channelId: Uuid.optional(),
  archetype: z.string().trim().min(1).max(64).optional(),
  runId: Uuid.optional(),
  /** Only posts with a time in this range: the calendar's window. */
  scheduledFrom: IsoDateTime.optional(),
  scheduledTo: IsoDateTime.optional(),
});
export type QueueListQuery = z.input<typeof QueueListQuery>;

export const EditCreativeRequest = z
  .object({
    body: z.string().trim().min(1).max(10_000).optional(),
    hashtags: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
    linkUrl: PostLinkUrl.nullable().optional(),
    linkLabel: z.string().trim().max(60).nullable().optional(),
    /** LinkedIn only: empty clears it. */
    firstComment: z.string().trim().max(FIRST_COMMENT_MAX).nullable().optional(),
    mediaAssetIds: z.array(Uuid).max(10).optional(),
    /** The revision the person was looking at: a change on top of someone else's change is refused. */
    revision: z.number().int().min(1).optional(),
  })
  .strict()
  .refine((value) => value.body !== undefined || value.hashtags !== undefined || value.linkUrl !== undefined || value.linkLabel !== undefined || value.firstComment !== undefined || value.mediaAssetIds !== undefined, { message: "nothing to change" });
export type EditCreativeRequest = z.input<typeof EditCreativeRequest>;

export const ApproveCreativeRequest = z
  .object({
    /** What the person saw. A yes for another text is not a yes for this one. */
    revision: z.number().int().min(1).optional(),
    scheduledFor: IsoDateTime.nullable().optional(),
  })
  .strict();
export type ApproveCreativeRequest = z.input<typeof ApproveCreativeRequest>;

export const RejectCreativeRequest = z.object({ reason: Reason, revision: z.number().int().min(1).optional() }).strict();
export type RejectCreativeRequest = z.input<typeof RejectCreativeRequest>;

export const BulkApproveRequest = z.object({ ids: z.array(Uuid).min(1).max(100), scheduledFor: IsoDateTime.nullable().optional() }).strict();
export type BulkApproveRequest = z.input<typeof BulkApproveRequest>;

export const BulkApproveResponse = z.object({
  results: z.array(z.object({ id: Uuid, ok: z.boolean(), status: CreativeStatus.nullable(), errorCode: z.string().nullable() })),
});
export type BulkApproveResponse = z.infer<typeof BulkApproveResponse>;

export const ScheduleCreativeRequest = z.object({ scheduledFor: IsoDateTime.nullable() }).strict();
export const WithdrawCreativeRequest = z
  .object({
    reason: Reason.optional(),
    /** A person looked and the post is gone from the platform, so only the record changes. */
    confirmedGone: z.boolean().optional(),
  })
  .strict();
export type WithdrawCreativeRequest = z.infer<typeof WithdrawCreativeRequest>;

export const RegenerateCreativeRequest = z.object({ note: z.string().trim().min(3).max(1_000).optional() }).strict();

// ---------------------------------------------------------------------------
// Approval link
// ---------------------------------------------------------------------------

export const CreateApprovalLinkRequest = z
  .object({
    /** Someone outside the team, on content nothing regulates. Their name is what the journal records. */
    approverName: z.string().trim().min(2).max(120).optional(),
    /** A member, chosen by id: their name comes from the membership, whatever `approverName` says. The only way on regulated content. */
    approverUserId: Uuid.nullable().optional(),
  })
  .strict()
  .refine((value) => Boolean(value.approverName || value.approverUserId), { message: "approver_required" });
export type CreateApprovalLinkRequest = z.input<typeof CreateApprovalLinkRequest>;

export const ApprovalLinkDto = z.object({
  id: Uuid,
  creativeId: Uuid,
  url: z.string(),
  approverName: z.string(),
  expiresAt: IsoDateTime,
});
export type ApprovalLinkDto = z.infer<typeof ApprovalLinkDto>;

/** What the public page reads: the card and no more than the card. */
export const PublicApprovalDto = z.object({
  creativeId: Uuid,
  revision: z.number().int(),
  status: CreativeStatus,
  brandName: z.string(),
  channel: z.object({ platform: z.string(), name: z.string().nullable() }),
  language: z.string(),
  body: z.string(),
  bodyRendered: z.string(),
  hashtags: z.array(z.string()),
  firstComment: z.string().nullable(),
  media: z.array(z.object({ url: z.string(), kind: z.string(), altText: z.string().nullable() })),
  preview: z.unknown(),
  findings: z.array(CheckFindingDto),
  /** The rules it broke, by id: the page says why in the reader's language. */
  rules: z.array(z.object({ id: z.string(), severity: z.string(), match: z.string().nullable() })),
  blocksApproval: z.boolean(),
  /** No picture yet only because the run is still making one: nothing to fix, and approval waits. */
  visualPending: z.boolean(),
  scheduledFor: IsoDateTime.nullable(),
  approverName: z.string(),
  expiresAt: IsoDateTime,
  canDecide: z.boolean(),
});
export type PublicApprovalDto = z.infer<typeof PublicApprovalDto>;

export const PublicApproveRequest = z.object({ revision: z.number().int().min(1) }).strict();
export const PublicRejectRequest = z.object({ reason: Reason }).strict();
export const PublicEditRequest = z.object({ body: z.string().trim().min(1).max(10_000), hashtags: z.array(z.string().trim().min(1).max(100)).max(30).optional(), firstComment: z.string().trim().max(FIRST_COMMENT_MAX).nullable().optional() }).strict();

// ---------------------------------------------------------------------------
// Autonomy and settings
// ---------------------------------------------------------------------------

export const ArchetypeAutonomyDto = z.object({
  archetype: z.string(),
  /** The level this tenant runs it at now, after every downgrade. */
  level: z.enum(AUTONOMY_LEVELS),
  regulated: z.boolean(),
  cleanApprovals: z.number().int(),
  required: z.number().int(),
  agentApproves: z.boolean(),
  canOptIn: z.boolean(),
  /** Why not, as a stable code: `regulated`, `too_few_clean_approvals`, `level_from_plugin`. */
  blockedBy: z.string().nullable(),
  optedInAt: IsoDateTime.nullable(),
  optedInByName: z.string().nullable(),
  revokedAt: IsoDateTime.nullable(),
  revokedReason: z.string().nullable(),
  /** `person` turned it off on purpose; `rejection` came from a post being turned down. */
  revokedBy: z.enum(["person", "rejection"]).nullable(),
});
export type ArchetypeAutonomyDto = z.infer<typeof ArchetypeAutonomyDto>;

/**
 * `level` is the whole choice: a person only, the agent pre-checks, or the agent approves. `agentApproves` is the
 * older form of the same question and stays for callers that already send it; one of the two is required, checked by
 * the service so the object keeps being extendable.
 */
export const SetArchetypeAutonomyRequest = z.object({ agentApproves: z.boolean().optional(), level: z.enum(AUTONOMY_LEVELS).optional() }).strict();

export const ApproverContact = z
  .object({
    userId: z.string().trim().min(1).max(100).nullable().optional(),
    name: z.string().trim().min(2).max(120),
    email: z.email().nullable().optional(),
    telegram: z.string().trim().min(1).max(100).nullable().optional(),
    whatsapp: z.string().trim().min(5).max(30).nullable().optional(),
    sms: z.string().trim().min(5).max(30).nullable().optional(),
    push: z.string().trim().min(1).max(500).nullable().optional(),
    channels: z.array(NotifyChannelName).min(1).max(5),
  })
  .strict();
export type ApproverContact = z.infer<typeof ApproverContact>;

export const StudioSettingsDto = z.object({
  countries: z.array(z.string()),
  dailyAgentApprovalsPerChannel: z.number().int(),
  spotCheckPercent: z.number().int(),
  killSwitchRejections: z.number().int(),
  pausedArchetypes: z.array(z.string()),
  approvers: z.array(ApproverContact),
});
export type StudioSettingsDto = z.infer<typeof StudioSettingsDto>;

export const UpdateStudioSettingsRequest = z
  .object({
    countries: z.array(z.string().trim().length(2)).max(30).optional(),
    dailyAgentApprovalsPerChannel: z.number().int().min(0).max(20).optional(),
    spotCheckPercent: z.number().int().min(0).max(100).optional(),
    killSwitchRejections: z.number().int().min(1).max(20).optional(),
    pausedArchetypes: z.array(z.string().trim().min(1).max(64)).max(100).optional(),
    approvers: z.array(ApproverContact).max(30).optional(),
  })
  .strict();
export type UpdateStudioSettingsRequest = z.input<typeof UpdateStudioSettingsRequest>;

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

export const ScheduleSlotDef = z
  .object({ weekday: z.number().int().min(1).max(7), hour: z.number().int().min(0).max(23), minute: z.number().int().min(0).max(59).default(0) })
  .strict();
export type ScheduleSlotDef = z.infer<typeof ScheduleSlotDef>;

export const ArchetypeMixEntry = z.object({ archetype: z.string().trim().min(1).max(64), weight: z.number().int().min(1).max(100) }).strict();
export type ArchetypeMixEntry = z.infer<typeof ArchetypeMixEntry>;

export const ScheduleInput = z
  .object({
    channelId: Uuid,
    postsPerWeek: z.number().int().min(1).max(21),
    slots: z.array(ScheduleSlotDef).min(1).max(21),
    archetypeMix: z.array(ArchetypeMixEntry).max(30).default([]),
    languages: z.array(Language).min(1).max(6),
    variants: z.number().int().min(1).max(6).default(1),
    isActive: z.boolean().default(true),
  })
  .strict()
  .refine((value) => value.slots.length >= value.postsPerWeek, { message: "fewer time slots than posts a week", path: ["slots"] });
export type ScheduleInput = z.input<typeof ScheduleInput>;

export const SchedulePatch = z
  .object({
    postsPerWeek: z.number().int().min(1).max(21),
    slots: z.array(ScheduleSlotDef).min(1).max(21),
    archetypeMix: z.array(ArchetypeMixEntry).max(30),
    languages: z.array(Language).min(1).max(6),
    variants: z.number().int().min(1).max(6),
    isActive: z.boolean(),
  })
  .partial()
  .strict();
export type SchedulePatch = z.input<typeof SchedulePatch>;

export const ScheduleDto = z.object({
  id: Uuid,
  channelId: Uuid,
  postsPerWeek: z.number().int(),
  slots: z.array(ScheduleSlotDef),
  archetypeMix: z.array(ArchetypeMixEntry),
  languages: z.array(z.string()),
  variants: z.number().int(),
  isActive: z.boolean(),
  lastFilledAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type ScheduleDto = z.infer<typeof ScheduleDto>;
export const SchedulePage = pageOf(ScheduleDto);

export const ScheduleFillDto = z.object({
  id: Uuid,
  scheduleId: Uuid,
  at: IsoDateTime,
  status: z.enum(["filled", "hole"]),
  reason: z.string().nullable(),
  slotKind: z.enum(["high", "exploration"]),
  topicId: Uuid.nullable(),
  archetype: z.string().nullable(),
  runId: Uuid.nullable(),
});
export type ScheduleFillDto = z.infer<typeof ScheduleFillDto>;

export const FillScheduleRequest = z.object({ weekStart: z.iso.date().optional() }).strict();
export const FillScheduleResponse = z.object({ filled: z.array(ScheduleFillDto), holes: z.array(ScheduleFillDto) });
export type FillScheduleResponse = z.infer<typeof FillScheduleResponse>;
export const ScheduleFillListQuery = z.object({ from: IsoDateTime.optional(), to: IsoDateTime.optional(), status: z.enum(["filled", "hole"]).optional() });

// ---------------------------------------------------------------------------
// Good hours
// ---------------------------------------------------------------------------

export const GOOD_HOUR_SOURCES = ["results", "sector", "sales", "schedule", "default"] as const;

/** An hour worth posting at, and what says so. A null weekday means every day. */
export const GoodHourDto = z.object({ weekday: z.number().int().min(1).max(7).nullable(), hour: z.number().int().min(0).max(23), source: z.enum(GOOD_HOUR_SOURCES) });
export type GoodHourDto = z.infer<typeof GoodHourDto>;
export const GoodHoursResponse = z.object({ items: z.array(GoodHourDto) });
export type GoodHoursResponse = z.infer<typeof GoodHoursResponse>;

// ---------------------------------------------------------------------------
// Own templates
// ---------------------------------------------------------------------------

export const VisualTemplateCard = z
  .object({
    template: z.string().trim().min(1).max(40),
    format: z.string().trim().min(1).max(10).default("1:1"),
    theme: z.string().trim().min(1).max(20).default("light"),
    /** The other shapes it may be drawn in. The one a channel prefers wins when it is here; otherwise `format`. */
    formats: z.array(z.string().trim().min(1).max(10)).max(8).optional(),
    /** Slots fixed by this template. The rest come from the generated text, by name. */
    slots: z.record(z.string(), z.unknown()).default({}),
    /** What the preview shows in the slots left to each post. Never published. */
    sample: z.record(z.string(), z.unknown()).default({}),
    /** Takes the place of the plain card on posts nobody chose a picture for. */
    automatic: z.boolean().default(true),
  })
  .strict();
export type VisualTemplateCard = z.infer<typeof VisualTemplateCard>;

/** What the same checks a post goes through say of a template's own words. */
export const TemplateCheckDto = z.object({
  blocks: z.boolean(),
  compliance: z.object({ status: z.string(), violations: z.array(z.object({ id: z.string(), severity: z.string(), match: z.string() })) }),
  /** Numbers the approved facts do not hold. */
  numbers: z.array(z.string()),
  /** Words the brand forbids. */
  forbidden: z.array(z.string()),
});
export type TemplateCheckDto = z.infer<typeof TemplateCheckDto>;

export const TemplateInput = z
  .object({
    kind: TemplateKind,
    name: z.string().trim().min(1).max(120),
    language: Language.nullable().optional(),
    body: z.string().trim().min(1).max(2_000).nullable().optional(),
    hashtags: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
    card: VisualTemplateCard.nullable().optional(),
    /** Agency-wide: offered to every tenant of the agency. Needs a role on the agency. */
    agencyWide: z.boolean().default(false),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.kind === "visual" && !value.card) ctx.addIssue({ code: "custom", message: "a visual template needs a card", path: ["card"] });
    if (value.kind === "hashtag_set" && !value.hashtags?.length) ctx.addIssue({ code: "custom", message: "a hashtag set needs hashtags", path: ["hashtags"] });
    if ((value.kind === "hook" || value.kind === "button" || value.kind === "signature") && !value.body) ctx.addIssue({ code: "custom", message: "this template needs a body", path: ["body"] });
  });
export type TemplateInput = z.input<typeof TemplateInput>;

export const TemplateCheckInput = z
  .object({
    language: Language.nullable().optional(),
    body: z.string().trim().max(2_000).nullable().optional(),
    hashtags: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
    card: VisualTemplateCard.nullable().optional(),
    /** Going on the shelf: its numbers answer to each tenant's facts when used, not to this one's now. */
    agencyWide: z.boolean().default(false),
  })
  .strict();
export type TemplateCheckInput = z.input<typeof TemplateCheckInput>;

export const TemplatePatch = z
  .object({
    name: z.string().trim().min(1).max(120),
    language: Language.nullable(),
    body: z.string().trim().min(1).max(2_000).nullable(),
    hashtags: z.array(z.string().trim().min(1).max(100)).max(30),
    card: VisualTemplateCard.nullable(),
    status: z.enum(["active", "archived"]),
  })
  .partial()
  .strict();
export type TemplatePatch = z.input<typeof TemplatePatch>;

export const TemplateDto = z.object({
  id: Uuid,
  kind: TemplateKind,
  name: z.string(),
  language: z.string().nullable(),
  body: z.string().nullable(),
  hashtags: z.array(z.string()),
  card: VisualTemplateCard.nullable(),
  status: z.enum(["active", "archived"]),
  /** tenant | agency: an agency's template is read-only here. */
  owner: z.enum(["tenant", "agency"]),
  /** The {{slot}} names in the body. */
  slots: z.array(z.string()),
  /** How it fares here today: filled in lists, where a shelf item meets this tenant's rules and facts. */
  check: TemplateCheckDto.nullable(),
  createdAt: IsoDateTime,
});
export type TemplateDto = z.infer<typeof TemplateDto>;
export const TemplatePage = pageOf(TemplateDto);
/** The agency above this tenant, and whether this person may put things on its shelf. */
export const TemplateShelfDto = z.object({ agencyName: z.string().nullable(), canPublish: z.boolean() });
export type TemplateShelfDto = z.infer<typeof TemplateShelfDto>;
export const TemplateListQuery = z.object({ kind: TemplateKind.optional(), language: Language.optional(), includeArchived: z.coerce.boolean().optional() });

export const TemplatePreviewRequest = z.object({ values: z.record(z.string(), z.string().max(500)).default({}), locale: Language.default("ro") }).strict();
export const TemplatePreviewDto = z.object({
  text: z.string().nullable(),
  hashtags: z.array(z.string()),
  missingSlots: z.array(z.string()),
  /** For a visual template: the card as a PNG in a data URL. */
  image: z.string().nullable(),
});
export type TemplatePreviewDto = z.infer<typeof TemplatePreviewDto>;
